// BL-2437 (ADR 033 — Transition Service): the Draft ↔ Upcoming rule over the four facts a phone
// holds offline (lot phase kind, home lot or not, auction phase kind, target). The server's batch
// check reaches it through `verdictForBatch`, and the offline check calls it directly: one rule.
import {
  canSetLotStatus,
  isLotStatusAllowed,
  type AuctionPhaseKind,
  type BatchApplyVerdict,
  type LotPhaseKind,
} from '@auctogon/core/isomorphic/lifecycle';

/** The inputs of {@link setStatusVerdict}. */
export interface SetStatusVerdictInput {
  /** The lot's ADR 023 phase kind; `null` when it has none (a live-webcast lot). */
  lotPhaseKind: LotPhaseKind | null;
  /** The lot is in no auction (ADR 028's home lot). */
  isHomeLot: boolean;
  /** The parent auction's phase kind; `null` for a home lot (no ceiling applies). */
  auctionPhaseKind: AuctionPhaseKind | null;
  /** The status asked for. */
  target: 'draft' | 'ready';
}

/**
 * Whether a lot may be set to Draft or Upcoming. Order is the server's: no derivable phase
 * (`unsupported-live`), then a home lot's Upcoming (`not-in-auction`, BUG-1110), then the matrix's
 * terminal and ceiling rungs (`canSetLotStatus`). Bids are not an input (BUG-1530).
 *
 * @param input - See {@link SetStatusVerdictInput}.
 * @returns The verdict.
 */
export function setStatusVerdict(input: SetStatusVerdictInput): BatchApplyVerdict {
  if (input.lotPhaseKind == null) return { allowed: false, reason: 'unsupported-live' };
  if (input.isHomeLot && input.target === 'ready') return { allowed: false, reason: 'not-in-auction' };
  const auctionAllowsTargetStatus =
    input.auctionPhaseKind == null ? true : isLotStatusAllowed(input.target, input.auctionPhaseKind);
  return canSetLotStatus({ lotPhaseKind: input.lotPhaseKind, auctionAllowsTargetStatus });
}
